import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  FileText,
  Eye,
  Send,
  CheckCircle,
  XCircle,
  Ban,
  Bell,
  Clock,
  PenTool,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { esignApi } from "../../services/api";

interface Props {
  onCreateEnvelope: () => void;
}

interface Signer {
  role: string;
  name: string;
  email: string;
  order: number;
  status: string;
  signedAt?: string;
}

interface EnvelopeData {
  _id: string;
  subject: string;
  message?: string;
  status: "draft" | "sent" | "viewed" | "signed" | "voided" | "expired";
  matterId?: { _id: string; title?: string } | string;
  signers: Signer[];
  documents: any[];
  createdAt: string;
  updatedAt?: string;
}

interface ActivityEntry {
  action: string;
  envelopeId?: string;
  subject?: string;
  description?: string;
  timestamp?: string;
  at?: string;
  createdAt?: string;
}

const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
  draft: { label: "Draft", bg: "bg-slate-100", text: "text-slate-600" },
  sent: { label: "Sent", bg: "bg-blue-100", text: "text-blue-700" },
  viewed: { label: "Viewed", bg: "bg-amber-100", text: "text-amber-700" },
  signed: { label: "Signed", bg: "bg-emerald-100", text: "text-emerald-700" },
  voided: { label: "Voided", bg: "bg-red-100", text: "text-red-700" },
  expired: { label: "Expired", bg: "bg-slate-100", text: "text-slate-500" },
};

const activityColors: Record<string, string> = {
  created: "bg-slate-400",
  sent: "bg-blue-500",
  viewed: "bg-amber-500",
  signed: "bg-emerald-500",
  declined: "bg-red-500",
  voided: "bg-red-500",
  completed: "bg-emerald-500",
  reminder_sent: "bg-purple-500",
};

const activityIcons: Record<string, React.ReactNode> = {
  created: <PenTool className="h-3.5 w-3.5 text-white" />,
  sent: <Send className="h-3.5 w-3.5 text-white" />,
  viewed: <Eye className="h-3.5 w-3.5 text-white" />,
  signed: <CheckCircle className="h-3.5 w-3.5 text-white" />,
  declined: <XCircle className="h-3.5 w-3.5 text-white" />,
  voided: <Ban className="h-3.5 w-3.5 text-white" />,
  completed: <CheckCircle className="h-3.5 w-3.5 text-white" />,
  reminder_sent: <Bell className="h-3.5 w-3.5 text-white" />,
};

function relativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatActionLabel(action: string): string {
  switch (action) {
    case "created": return "Request created";
    case "sent": return "Request sent";
    case "viewed": return "Request viewed";
    case "signed": return "Signer signed";
    case "declined": return "Signer declined";
    case "voided": return "Request voided";
    case "completed": return "All signatures completed";
    case "reminder_sent": return "Reminder sent";
    default: return action.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  }
}

const PRIMARY = "var(--color-primary)";
const HOVER = "var(--color-primary-hover)";

const EsignOverviewTab: React.FC<Props> = ({ onCreateEnvelope }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attentionEnvelopes, setAttentionEnvelopes] = useState<EnvelopeData[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityEntry[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [sentRes, viewedRes, dashRes] = await Promise.all([
          esignApi.list({ status: "sent", limit: 10 }),
          esignApi.list({ status: "viewed", limit: 10 }),
          esignApi.dashboard(),
        ]);

        if (cancelled) return;

        const sentEnvelopes: EnvelopeData[] = sentRes.data?.data || [];
        const viewedEnvelopes: EnvelopeData[] = viewedRes.data?.data || [];

        // Combine and de-duplicate
        const map = new Map<string, EnvelopeData>();
        [...viewedEnvelopes, ...sentEnvelopes].forEach((env) => {
          map.set(env._id, env);
        });
        setAttentionEnvelopes(Array.from(map.values()));

        const dashData = dashRes.data?.data || dashRes.data;
        setRecentActivity(dashData?.recentActivity || []);
      } catch (err) {
        console.error("Failed to fetch overview data:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderColor: PRIMARY }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={onCreateEnvelope}
          className="flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-xl transition-colors"
          style={{ backgroundColor: PRIMARY }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = HOVER;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = PRIMARY;
          }}
        >
          <Plus className="h-4 w-4" />
          New Request
        </button>
        <button
          onClick={() => navigate("/esign?tab=envelopes")}
          className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors"
        >
          <FileText className="h-4 w-4" />
          View All Requests
        </button>
      </div>

      {/* Needs Attention */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-800">Needs Attention</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Requests waiting for signatures or that have been viewed
          </p>
        </div>

        {attentionEnvelopes.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-emerald-50 mb-4">
              <CheckCircle className="h-7 w-7 text-emerald-500" />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">All caught up!</h3>
            <p className="text-sm text-slate-500">No requests need attention.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {attentionEnvelopes.map((envelope, i) => {
              const config = statusConfig[envelope.status] || statusConfig.draft;
              const signedCount =
                envelope.signers?.filter((s) => s.status === "signed").length || 0;
              const totalSigners = envelope.signers?.length || 0;

              return (
                <motion.div
                  key={envelope._id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => navigate(`/esign/${envelope._id}`)}
                  className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="flex-shrink-0">
                      <div
                        className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                          envelope.status === "viewed" ? "bg-amber-50" : "bg-blue-50"
                        }`}
                      >
                        {envelope.status === "viewed" ? (
                          <Eye className="h-5 w-5 text-amber-600" />
                        ) : (
                          <Send className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {envelope.subject}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
                        >
                          {config.label}
                        </span>
                        <span className="text-xs text-slate-500">
                          {signedCount}/{totalSigners} signed
                        </span>
                        <span className="text-xs text-slate-400">
                          {relativeTime(envelope.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-800">Recent Activity</h2>
          <p className="text-sm text-slate-500 mt-0.5">Latest signature events across all requests</p>
        </div>

        {recentActivity.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-slate-100 mb-4">
              <Clock className="h-7 w-7 text-slate-400" />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">No activity yet</h3>
            <p className="text-sm text-slate-500 mb-5">
              Create your first signing request to get started.
            </p>
            <button
              onClick={onCreateEnvelope}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-xl transition-colors"
              style={{ backgroundColor: PRIMARY }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = HOVER;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = PRIMARY;
              }}
            >
              <Plus className="h-4 w-4" />
              New Request
            </button>
          </div>
        ) : (
          <div className="px-6 py-4">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[15px] top-2 bottom-2 w-px bg-slate-200" />

              <div className="space-y-5">
                {recentActivity.slice(0, 15).map((entry, i) => {
                  const dotColor = activityColors[entry.action] || "bg-slate-400";
                  const icon = activityIcons[entry.action] || (
                    <Sparkles className="h-3.5 w-3.5 text-white" />
                  );
                  const timestamp = entry.at || entry.timestamp || entry.createdAt || "";

                  return (
                    <motion.div
                      key={`${entry.action}-${timestamp}-${i}`}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="flex items-start gap-4 relative"
                    >
                      {/* Dot */}
                      <div
                        className={`relative z-10 flex items-center justify-center h-[30px] w-[30px] rounded-full ${dotColor} flex-shrink-0`}
                      >
                        {icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pt-1">
                        <p className="text-sm text-slate-800">
                          <span className="font-medium">{formatActionLabel(entry.action)}</span>
                          {entry.subject && (
                            <>
                              {" "}
                              &mdash;{" "}
                              <span className="text-slate-600">{entry.subject}</span>
                            </>
                          )}
                          {entry.description && !entry.subject && (
                            <>
                              {" "}
                              &mdash;{" "}
                              <span className="text-slate-600">{entry.description}</span>
                            </>
                          )}
                        </p>
                        {timestamp && (
                          <p className="text-xs text-slate-400 mt-0.5">
                            {relativeTime(timestamp)}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EsignOverviewTab;
